import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Apple, Coffee, UtensilsCrossed, Cookie, Plus, Edit2, Trash2, 
  Target, TrendingUp, Calendar, Search, BarChart3, Droplets, GlassWater, Star
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Water amount presets in ml
const WATER_PRESETS = [
  { label: 'Glas', amount: 250, icon: '🥛' },
  { label: 'Becher', amount: 300, icon: '☕' },
  { label: 'Flasche', amount: 500, icon: '🍶' },
  { label: 'Grosse Flasche', amount: 750, icon: '🧴' },
];

const MEAL_TYPES = {
  breakfast: { label: 'Frühstück', icon: Coffee, color: 'bg-amber-100 text-amber-700', emoji: '🌅' },
  snack_morning: { label: 'Snack (Vormittag)', icon: Cookie, color: 'bg-orange-50 text-orange-600', emoji: '🥨' },
  lunch: { label: 'Mittagessen', icon: UtensilsCrossed, color: 'bg-green-100 text-green-700', emoji: '☀️' },
  snack_afternoon: { label: 'Snack (Nachmittag)', icon: Cookie, color: 'bg-yellow-50 text-yellow-600', emoji: '🍎' },
  dinner: { label: 'Abendessen', icon: UtensilsCrossed, color: 'bg-blue-100 text-blue-700', emoji: '🌙' },
  snack_evening: { label: 'Snack (Abend)', icon: Cookie, color: 'bg-purple-50 text-purple-600', emoji: '🍪' },
  // Legacy support
  snack: { label: 'Snack', icon: Cookie, color: 'bg-purple-100 text-purple-700', emoji: '🍎' },
  breakfast_2: { label: 'Frühstück 2', icon: Coffee, color: 'bg-amber-100 text-amber-700', emoji: '🌅' },
  lunch_2: { label: 'Mittagessen 2', icon: UtensilsCrossed, color: 'bg-green-100 text-green-700', emoji: '☀️' },
  dinner_2: { label: 'Abendessen 2', icon: UtensilsCrossed, color: 'bg-blue-100 text-blue-700', emoji: '🌙' },
  snack_2: { label: 'Snack 2', icon: Cookie, color: 'bg-purple-100 text-purple-700', emoji: '🍎' },
  snack_3: { label: 'Snack 3', icon: Cookie, color: 'bg-purple-100 text-purple-700', emoji: '🍎' }
};

// Visible meal slots configuration
const DEFAULT_MEAL_SLOTS = ['breakfast', 'snack_morning', 'lunch', 'snack_afternoon', 'dinner', 'snack_evening'];

const CustomerNutrition = ({ customerId, customerName }) => {
  const [entries, setEntries] = useState([]);
  const [goals, setGoals] = useState(null);
  const [summary, setSummary] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [waterData, setWaterData] = useState({ total_ml: 0, goal_ml: 2500, entries: [] });
  const [savedMeals, setSavedMeals] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [goalsDialogOpen, setGoalsDialogOpen] = useState(false);
  const [savedMealsDialogOpen, setSavedMealsDialogOpen] = useState(false);
  const [saveMealDialogOpen, setSaveMealDialogOpen] = useState(false);
  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  const [portionDialogOpen, setPortionDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [portionAmount, setPortionAmount] = useState('');
  const [portionMealType, setPortionMealType] = useState('lunch');
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [visibleMealSlots, setVisibleMealSlots] = useState(DEFAULT_MEAL_SLOTS);
  const [newMealName, setNewMealName] = useState('');
  const [selectedEntriesForMeal, setSelectedEntriesForMeal] = useState([]);
  const [glucoseTrackingEnabled, setGlucoseTrackingEnabled] = useState(false); // For time input
  
  // Recipe creation state
  const [newRecipeName, setNewRecipeName] = useState('');
  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [ingredientSearchResults, setIngredientSearchResults] = useState([]);
  
  const [formData, setFormData] = useState({
    date: selectedDate,
    meal_type: 'lunch',
    meal_time: '', // Time for glucose correlation
    food_name: '',
    brand: '',
    serving_size: 100,
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    fiber: '',
    notes: ''
  });

  const [goalsForm, setGoalsForm] = useState({
    daily_calories: 2000,
    daily_protein: 150,
    daily_carbs: 200,
    daily_fat: 70,
    daily_water: 2500
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, goalsRes, weeklyRes, waterRes, glucoseSettingsRes] = await Promise.all([
        axios.get(`${API}/nutrition/customer/${customerId}/summary?date_str=${selectedDate}`, { headers }),
        axios.get(`${API}/nutrition/customer/${customerId}/goals`, { headers }),
        axios.get(`${API}/nutrition/customer/${customerId}/weekly-stats`, { headers }),
        axios.get(`${API}/nutrition/customer/${customerId}/water?date_str=${selectedDate}`, { headers }),
        axios.get(`${API}/glucose/settings?customer_id=${customerId}`, { headers }).catch(() => ({ data: { enabled: false } }))
      ]);

      setSummary(summaryRes.data);
      setGoals(goalsRes.data);
      setWeeklyStats(weeklyRes.data);
      setWaterData(waterRes.data);
      setGlucoseTrackingEnabled(glucoseSettingsRes.data?.enabled || false);

      // Iter 329: visibleMealSlots dynamisch erweitern, falls Einträge in
      // Meal-Types existieren, die nicht in DEFAULT_MEAL_SLOTS sind (z.B.
      // Legacy 'snack', oder Snack 2/3 aus älteren App-Versionen). So
      // landet kein getrackter Eintrag in der "tot-Zone" ohne Anzeige.
      try {
        const byMeal = summaryRes.data?.by_meal || {};
        const extraSlots = Object.keys(byMeal).filter(
          (k) => (byMeal[k]?.entries?.length || 0) > 0 && !DEFAULT_MEAL_SLOTS.includes(k) && MEAL_TYPES[k]
        );
        if (extraSlots.length > 0) {
          setVisibleMealSlots((prev) => {
            const merged = [...prev];
            extraSlots.forEach((s) => {
              if (!merged.includes(s)) merged.push(s);
            });
            return merged;
          });
        }
      } catch {
        /* best-effort */
      }
      
      if (goalsRes.data) {
        setGoalsForm({
          daily_calories: goalsRes.data.daily_calories || 2000,
          daily_protein: goalsRes.data.daily_protein || 150,
          daily_carbs: goalsRes.data.daily_carbs || 200,
          daily_fat: goalsRes.data.daily_fat || 70,
          daily_water: goalsRes.data.daily_water || 2500
        });
      }
    } catch (error) {
      console.error('Error fetching nutrition data:', error);
    } finally {
      setLoading(false);
    }
  }, [customerId, selectedDate]);

  useEffect(() => {
    if (customerId) {
      fetchData();
      fetchSavedMeals();
      fetchRecipes();
    }
  }, [customerId]);

  // Re-fetch when date changes
  useEffect(() => {
    if (customerId) {
      fetchData();
    }
  }, [selectedDate]);

  // Recipe functions
  const fetchRecipes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/nutrition/customer/${customerId}/recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  const searchIngredient = async (query) => {
    if (!query || query.length < 2) {
      setIngredientSearchResults([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/portal/nutrition/search?query=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIngredientSearchResults(response.data.products || []);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const addIngredientToRecipe = (product, amount_g = 100) => {
    const nutriments = product.nutriments || {};
    const nutrition = product.nutrition_per_100g || {};
    const factor = amount_g / 100;
    
    const calories_per_100g = nutriments['energy-kcal_100g'] || nutrition.calories || 0;
    const protein_per_100g = nutriments.proteins_100g || nutrition.protein || 0;
    const carbs_per_100g = nutriments.carbohydrates_100g || nutrition.carbs || 0;
    const fat_per_100g = nutriments.fat_100g || nutrition.fat || 0;
    
    const ingredient = {
      id: Date.now().toString(),
      food_name: product.product_name || product.name,
      brand: product.brands || product.brand || '',
      amount_g: amount_g,
      calories: Math.round(calories_per_100g * factor),
      protein: Math.round(protein_per_100g * factor * 10) / 10,
      carbs: Math.round(carbs_per_100g * factor * 10) / 10,
      fat: Math.round(fat_per_100g * factor * 10) / 10,
      calories_per_100g,
      protein_per_100g,
      carbs_per_100g,
      fat_per_100g
    };
    
    setRecipeIngredients([...recipeIngredients, ingredient]);
    setIngredientSearch('');
    setIngredientSearchResults([]);
  };

  const updateIngredientAmount = (ingredientId, newAmount) => {
    setRecipeIngredients(recipeIngredients.map(ing => {
      if (ing.id === ingredientId) {
        const factor = newAmount / 100;
        return {
          ...ing,
          amount_g: newAmount,
          calories: Math.round((ing.calories_per_100g || 0) * factor),
          protein: Math.round((ing.protein_per_100g || 0) * factor * 10) / 10,
          carbs: Math.round((ing.carbs_per_100g || 0) * factor * 10) / 10,
          fat: Math.round((ing.fat_per_100g || 0) * factor * 10) / 10
        };
      }
      return ing;
    }));
  };

  const removeIngredientFromRecipe = (ingredientId) => {
    setRecipeIngredients(recipeIngredients.filter(ing => ing.id !== ingredientId));
  };

  const saveRecipe = async () => {
    if (!newRecipeName.trim() || recipeIngredients.length === 0) {
      toast.error('Bitte Name eingeben und mindestens eine Zutat hinzufügen');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/nutrition/customer/${customerId}/recipes`,
        { name: newRecipeName, ingredients: recipeIngredients },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Mahlzeit erstellt');
      setRecipeDialogOpen(false);
      setNewRecipeName('');
      setRecipeIngredients([]);
      fetchRecipes();
    } catch (error) {
      console.error('Error creating recipe:', error);
      toast.error('Fehler beim Erstellen');
    }
  };

  const takePortionFromRecipe = async () => {
    if (!selectedRecipe || !portionAmount) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/nutrition/recipes/${selectedRecipe.id}/take-portion?portion_g=${portionAmount}&meal_type=${portionMealType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${portionAmount}g hinzugefügt (${response.data.macros.calories} kcal)`);
      setPortionDialogOpen(false);
      setSelectedRecipe(null);
      setPortionAmount('');
      fetchData();
      fetchRecipes();
    } catch (error) {
      console.error('Error taking portion:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const resetRecipe = async (recipeId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/nutrition/recipes/${recipeId}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mahlzeit aufgefüllt');
      fetchRecipes();
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const deleteRecipe = async (recipeId) => {
    if (!window.confirm('Mahlzeit wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/nutrition/recipes/${recipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mahlzeit gelöscht');
      fetchRecipes();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Water tracking functions
  const addWaterEntry = async (amount_ml) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/nutrition/customer/${customerId}/water?amount_ml=${amount_ml}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${amount_ml}ml Wasser hinzugefügt`);
      fetchData();
    } catch (error) {
      console.error('Error adding water:', error);
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const deleteWaterEntry = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/nutrition/water/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eintrag gelöscht');
      fetchData();
    } catch (error) {
      console.error('Error deleting water entry:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Saved meals functions
  const fetchSavedMeals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/nutrition/customer/${customerId}/saved-meals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedMeals(response.data);
    } catch (error) {
      console.error('Error fetching saved meals:', error);
    }
  };

  const createSavedMeal = async () => {
    if (!newMealName.trim() || selectedEntriesForMeal.length === 0) {
      toast.error('Bitte Name eingeben und mindestens einen Eintrag auswählen');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const items = selectedEntriesForMeal.map(entry => ({
        food_name: entry.food_name,
        brand: entry.brand,
        serving_size: entry.serving_size,
        calories: entry.calories,
        protein: entry.protein,
        carbs: entry.carbs,
        fat: entry.fat,
        fiber: entry.fiber
      }));
      
      await axios.post(
        `${API}/nutrition/customer/${customerId}/saved-meals`,
        { name: newMealName, items },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Mahlzeit gespeichert');
      setSaveMealDialogOpen(false);
      setNewMealName('');
      setSelectedEntriesForMeal([]);
      fetchSavedMeals();
    } catch (error) {
      console.error('Error creating saved meal:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const useSavedMeal = async (mealId, mealType = 'lunch') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/nutrition/customer/${customerId}/saved-meals/${mealId}/use?meal_type=${mealType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mahlzeit hinzugefügt');
      setSavedMealsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error using saved meal:', error);
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const deleteSavedMeal = async (mealId) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/nutrition/saved-meals/${mealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vorlage gelöscht');
      fetchSavedMeals();
    } catch (error) {
      console.error('Error deleting saved meal:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Add additional meal slot
  const addMealSlot = (baseType) => {
    const existingSlots = visibleMealSlots.filter(s => s.startsWith(baseType));
    const nextSlot = `${baseType}_${existingSlots.length + 1}`;
    if (MEAL_TYPES[nextSlot]) {
      setVisibleMealSlots([...visibleMealSlots, nextSlot]);
    }
  };

  const searchFood = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      setSearching(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/portal/nutrition/search?query=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchResults(response.data.products || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const selectSearchResult = (product) => {
    setFormData({
      ...formData,
      food_name: product.product_name || product.name,
      brand: product.brands || '',
      calories: product.nutriments?.['energy-kcal_100g'] || '',
      protein: product.nutriments?.proteins_100g || '',
      carbs: product.nutriments?.carbohydrates_100g || '',
      fat: product.nutriments?.fat_100g || '',
      fiber: product.nutriments?.fiber_100g || ''
    });
    setSearchResults([]);
    setSearchQuery('');
  };

  const saveEntry = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const data = {
        ...formData,
        date: selectedDate,
        serving_size: parseFloat(formData.serving_size) || 100,
        calories: formData.calories ? parseInt(formData.calories) : null,
        protein: formData.protein ? parseFloat(formData.protein) : null,
        carbs: formData.carbs ? parseFloat(formData.carbs) : null,
        fat: formData.fat ? parseFloat(formData.fat) : null,
        fiber: formData.fiber ? parseFloat(formData.fiber) : null
      };

      if (editingEntry) {
        await axios.put(`${API}/nutrition/entries/${editingEntry.id}`, data, { headers });
        toast.success('Eintrag aktualisiert');
      } else {
        await axios.post(`${API}/nutrition/customer/${customerId}/entries`, data, { headers });
        toast.success('Eintrag hinzugefügt');
      }

      setDialogOpen(false);
      setEditingEntry(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/nutrition/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eintrag gelöscht');
      fetchData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const saveGoals = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/nutrition/customer/${customerId}/goals`,
        {
          mode: 'manual',
          ...goalsForm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ziele gespeichert');
      setGoalsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving goals:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const resetForm = () => {
    setFormData({
      date: selectedDate,
      meal_type: 'lunch',
      food_name: '',
      brand: '',
      serving_size: 100,
      calories: '',
      protein: '',
      carbs: '',
      fat: '',
      fiber: '',
      notes: ''
    });
  };

  const openAddDialog = (mealType = 'lunch') => {
    resetForm();
    setFormData(prev => ({ ...prev, meal_type: mealType }));
    setEditingEntry(null);
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setFormData({
      date: entry.date,
      meal_type: entry.meal_type,
      food_name: entry.food_name || '',
      brand: entry.brand || '',
      serving_size: entry.serving_size || 100,
      calories: entry.calories || '',
      protein: entry.protein || '',
      carbs: entry.carbs || '',
      fat: entry.fat || '',
      fiber: entry.fiber || '',
      notes: entry.notes || ''
    });
    setEditingEntry(entry);
    setDialogOpen(true);
  };

  // Calculate progress percentages
  const getProgress = useCallback((current, target) => {
    if (!target) return 0;
    return Math.min(Math.round((current / target) * 100), 100);
  }, []);

  const totals = useMemo(() => summary?.totals || { calories: 0, protein: 0, carbs: 0, fat: 0 }, [summary]);
  const targets = useMemo(() => goals || goalsForm, [goals, goalsForm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8" data-testid="customer-nutrition-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" data-testid="customer-nutrition-loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="customer-nutrition-tab">
      {/* Header */}
      <div className="flex justify-between items-center" data-testid="nutrition-header">
        <div>
          <h3 className="text-lg font-semibold text-gray-900" data-testid="nutrition-title">Ernährung</h3>
          <p className="text-sm text-gray-500" data-testid="nutrition-customer-name">{customerName}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="nutrition-header-actions">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-40"
            data-testid="nutrition-date-input"
          />
          <Button variant="outline" size="sm" onClick={() => { fetchSavedMeals(); setSavedMealsDialogOpen(true); }} data-testid="nutrition-saved-meals-button">
            <Apple className="w-4 h-4 mr-1" />
            Vorlagen
            {savedMeals.length > 0 && (
              <Badge variant="secondary" className="ml-1" data-testid="nutrition-saved-meals-count">{savedMeals.length}</Badge>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setGoalsDialogOpen(true)} data-testid="nutrition-goals-button">
            <Target className="w-4 h-4 mr-1" />
            Ziele
          </Button>
          <Button size="sm" onClick={() => openAddDialog()} data-testid="nutrition-add-entry-button">
            <Plus className="w-4 h-4 mr-1" />
            Hinzufügen
          </Button>
        </div>
      </div>

      {/* Daily Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="nutrition-summary-grid">
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100" data-testid="nutrition-summary-calories">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-orange-700" data-testid="nutrition-summary-calories-label">Kalorien</span>
              <Apple className="w-4 h-4 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-orange-900" data-testid="nutrition-summary-calories-value">{totals.calories}</p>
            <p className="text-xs text-orange-600" data-testid="nutrition-summary-calories-target">/ {targets.daily_calories} kcal</p>
            <div className="mt-2 h-2 bg-orange-200 rounded-full overflow-hidden" data-testid="nutrition-summary-calories-progress">
              <div 
                className="h-full bg-orange-500 transition-all"
                style={{ width: `${getProgress(totals.calories, targets.daily_calories)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100" data-testid="nutrition-summary-protein">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-red-700" data-testid="nutrition-summary-protein-label">Protein</span>
              <span className="text-red-600 font-bold">P</span>
            </div>
            <p className="text-2xl font-bold text-red-900" data-testid="nutrition-summary-protein-value">{totals.protein?.toFixed(1) || 0}g</p>
            <p className="text-xs text-red-600" data-testid="nutrition-summary-protein-target">/ {targets.daily_protein}g</p>
            <div className="mt-2 h-2 bg-red-200 rounded-full overflow-hidden" data-testid="nutrition-summary-protein-progress">
              <div 
                className="h-full bg-red-500 transition-all"
                style={{ width: `${getProgress(totals.protein, targets.daily_protein)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100" data-testid="nutrition-summary-carbs">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-yellow-700" data-testid="nutrition-summary-carbs-label">Kohlenhydrate</span>
              <span className="text-yellow-600 font-bold">K</span>
            </div>
            <p className="text-2xl font-bold text-yellow-900" data-testid="nutrition-summary-carbs-value">{totals.carbs?.toFixed(1) || 0}g</p>
            <p className="text-xs text-yellow-600" data-testid="nutrition-summary-carbs-target">/ {targets.daily_carbs}g</p>
            <div className="mt-2 h-2 bg-yellow-200 rounded-full overflow-hidden" data-testid="nutrition-summary-carbs-progress">
              <div 
                className="h-full bg-yellow-500 transition-all"
                style={{ width: `${getProgress(totals.carbs, targets.daily_carbs)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100" data-testid="nutrition-summary-fat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-700" data-testid="nutrition-summary-fat-label">Fett</span>
              <span className="text-blue-600 font-bold">F</span>
            </div>
            <p className="text-2xl font-bold text-blue-900" data-testid="nutrition-summary-fat-value">{totals.fat?.toFixed(1) || 0}g</p>
            <p className="text-xs text-blue-600" data-testid="nutrition-summary-fat-target">/ {targets.daily_fat}g</p>
            <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden" data-testid="nutrition-summary-fat-progress">
              <div 
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${getProgress(totals.fat, targets.daily_fat)}%` }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Water Tracking Card */}
                        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100" data-testid="nutrition-water-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between" data-testid="nutrition-water-header">
            <CardTitle className="text-base flex items-center gap-2" data-testid="nutrition-water-title">
              <Droplets className="w-5 h-5 text-cyan-600" />
              Wasseraufnahme
            </CardTitle>
            <Badge variant="outline" className="text-cyan-700 border-cyan-300" data-testid="nutrition-water-badge">
              {(waterData.total_ml / 1000).toFixed(1)}L / {(waterData.goal_ml / 1000).toFixed(1)}L
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Progress bar */}
                                                                                                                      <div className="mb-3" data-testid="nutrition-water-progress">
            <div className="h-3 bg-cyan-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 transition-all duration-500"
                style={{ width: `${Math.min(waterData.percent || 0, 100)}%` }}
                data-testid="nutrition-water-progress-bar"
              />
            </div>
            <p className="text-xs text-cyan-700 mt-1 text-right" data-testid="nutrition-water-progress-percent">{waterData.percent || 0}%</p>
          </div>
          
          {/* Quick add buttons */}
          <div className="flex flex-wrap gap-2 mb-3">
            {WATER_PRESETS.map((preset) => (
              <Button
                key={preset.amount}
                variant="outline"
                size="sm"
                className="text-cyan-700 border-cyan-300 hover:bg-cyan-100"
                onClick={() => addWaterEntry(preset.amount)}
                data-testid={`nutrition-water-preset-${preset.amount}`}
              >
                {preset.icon} {preset.amount}ml
              </Button>
            ))}
          </div>
          
          {/* Today's entries */}
          {waterData.entries && waterData.entries.length > 0 && (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {waterData.entries.map((entry) => (
                                                                <div key={entry.id} className="flex items-center justify-between text-sm py-1 px-2 bg-white/50 rounded" data-testid={`nutrition-water-entry-${entry.id}`}>
                  <span className="text-cyan-800">
                    💧 {entry.amount_ml}ml um {entry.time}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                    onClick={() => deleteWaterEntry(entry.id)}
                    data-testid={`nutrition-water-entry-${entry.id}-delete`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for Meals and Stats */}
      <Tabs defaultValue="meals" className="w-full" data-testid="nutrition-main-tabs">
        <TabsList className="grid w-full grid-cols-4" data-testid="nutrition-main-tabs-list">
          <TabsTrigger value="meals" data-testid="nutrition-tab-meals">📅 Heute</TabsTrigger>
          <TabsTrigger value="recipes" data-testid="nutrition-tab-recipes">🍳 Mahlzeiten ({recipes.length})</TabsTrigger>
          <TabsTrigger value="saved" data-testid="nutrition-tab-saved">⭐ Vorlagen ({savedMeals.length})</TabsTrigger>
          <TabsTrigger value="stats" data-testid="nutrition-tab-stats">📊 Statistik</TabsTrigger>
        </TabsList>

                <TabsContent value="meals" className="space-y-3 mt-4" data-testid="nutrition-tab-content-meals">
          {/* Quick add slot buttons */}
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="text-xs text-gray-500 mr-2">Slot hinzufügen:</span>
            {!visibleMealSlots.includes('breakfast_2') && (
              <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => addMealSlot('breakfast')} data-testid="nutrition-add-slot-breakfast">
                + Frühstück
              </Button>
            )}
            {!visibleMealSlots.includes('lunch_2') && (
              <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => addMealSlot('lunch')} data-testid="nutrition-add-slot-lunch">
                + Mittagessen
              </Button>
            )}
            {!visibleMealSlots.includes('dinner_2') && (
              <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => addMealSlot('dinner')} data-testid="nutrition-add-slot-dinner">
                + Abendessen
              </Button>
            )}
            {!visibleMealSlots.includes('snack_2') && (
              <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => addMealSlot('snack')} data-testid="nutrition-add-slot-snack">
                + Snack
              </Button>
            )}
            {visibleMealSlots.includes('snack_2') && !visibleMealSlots.includes('snack_3') && (
              <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => addMealSlot('snack')} data-testid="nutrition-add-slot-snack-3">
                + Snack 3
              </Button>
            )}
          </div>

          {/* Meals by visible slots */}
          {visibleMealSlots.map((type) => {
            const config = MEAL_TYPES[type];
            if (!config) return null;
            
            const mealData = summary?.by_meal?.[type] || { entries: [], calories: 0 };
            const Icon = config.icon;
            
            return (
              <Card key={type} className="overflow-hidden" data-testid={`nutrition-meal-card-${type}`}>
                <CardHeader className="py-3 px-4 bg-gray-50 flex flex-row items-center justify-between" data-testid={`nutrition-meal-card-${type}-header`}>
                  <div className="flex items-center gap-2" data-testid={`nutrition-meal-card-${type}-info`}>
                    <div className={`p-1.5 rounded-lg ${config.color}`} data-testid={`nutrition-meal-card-${type}-icon`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <CardTitle className="text-sm font-medium" data-testid={`nutrition-meal-card-${type}-title`}>{config.label}</CardTitle>
                    {mealData.calories > 0 && (
                      <Badge variant="secondary" className="ml-2" data-testid={`nutrition-meal-card-${type}-calories`}>
                        {mealData.calories} kcal
                      </Badge>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => openAddDialog(type)} data-testid={`nutrition-meal-card-${type}-add`}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </CardHeader>
                
                {mealData.entries.length > 0 ? (
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {mealData.entries.map((entry) => (
                        <div key={entry.id} className="flex items-center justify-between p-3 hover:bg-gray-50" data-testid={`nutrition-meal-entry-${entry.id}`}>
                          <div className="flex-1" data-testid={`nutrition-meal-entry-${entry.id}-info`}>
                            <p className="font-medium text-sm" data-testid={`nutrition-meal-entry-${entry.id}-name`}>{entry.food_name}</p>
                            {entry.brand && (
                              <p className="text-xs text-gray-500" data-testid={`nutrition-meal-entry-${entry.id}-brand`}>{entry.brand}</p>
                            )}
                            <div className="flex gap-3 text-xs text-gray-500 mt-1" data-testid={`nutrition-meal-entry-${entry.id}-macros`}>
                              {entry.calories && <span>{entry.calories} kcal</span>}
                              {entry.protein && <span>P: {entry.protein}g</span>}
                              {entry.carbs && <span>K: {entry.carbs}g</span>}
                              {entry.fat && <span>F: {entry.fat}g</span>}
                            </div>
                          </div>
                          <div className="flex gap-1" data-testid={`nutrition-meal-entry-${entry.id}-actions`}>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(entry)} data-testid={`nutrition-meal-entry-${entry.id}-edit`}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500"
                              onClick={() => deleteEntry(entry.id)}
                              data-testid={`nutrition-meal-entry-${entry.id}-delete`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                ) : (
                  <CardContent className="py-4 text-center text-gray-400 text-sm" data-testid={`nutrition-meal-card-${type}-empty`}>
                    Noch keine Einträge
                  </CardContent>
                )}
              </Card>
            );
          })}
          
          {/* Save current day as meal button */}
          {summary?.entry_count > 0 && (
            <div className="text-center mt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  // Get all today's entries
                  const allEntries = Object.values(summary?.by_meal || {}).flatMap(m => m.entries || []);
                  setSelectedEntriesForMeal(allEntries);
                  setSaveMealDialogOpen(true);
                }}
                data-testid="nutrition-save-day-template-button"
              >
                <Star className="w-4 h-4 mr-2" />
                Heutige Einträge als Vorlage speichern
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Recipes Tab - Prepared Meals */}
        <TabsContent value="recipes" className="mt-4" data-testid="nutrition-tab-content-recipes">
          <Card data-testid="nutrition-recipes-card">
            <CardHeader className="py-3 flex flex-row items-center justify-between" data-testid="nutrition-recipes-header">
              <div>
                <CardTitle className="text-base" data-testid="nutrition-recipes-title">🍳 Zubereitete Mahlzeiten</CardTitle>
                <p className="text-xs text-gray-500" data-testid="nutrition-recipes-subtitle">Erstellen Sie Gerichte aus mehreren Zutaten und entnehmen Sie Portionen</p>
              </div>
              <Button size="sm" onClick={() => setRecipeDialogOpen(true)} data-testid="nutrition-recipes-new-button">
                <Plus className="w-4 h-4 mr-1" />
                Neue Mahlzeit
              </Button>
            </CardHeader>
            <CardContent>
              {recipes.length > 0 ? (
                <div className="space-y-4">
                  {recipes.map((recipe) => {
                    const percentRemaining = recipe.total_weight_g > 0 
                      ? Math.round((recipe.remaining_weight_g / recipe.total_weight_g) * 100) 
                      : 0;
                    
                    return (
                      <div key={recipe.id} className="p-4 bg-gray-50 rounded-lg border" data-testid={`nutrition-recipe-${recipe.id}`}>
                        <div className="flex items-start justify-between mb-3" data-testid={`nutrition-recipe-${recipe.id}-header`}>
                          <div>
                            <h4 className="font-semibold text-gray-900" data-testid={`nutrition-recipe-${recipe.id}-name`}>{recipe.name}</h4>
                            <p className="text-xs text-gray-500 mt-1" data-testid={`nutrition-recipe-${recipe.id}-meta`}>
                              {recipe.ingredients?.length || 0} Zutaten • Erstellt am {new Date(recipe.created_at).toLocaleDateString('de-CH')}
                            </p>
                          </div>
                          <div className="flex gap-1" data-testid={`nutrition-recipe-${recipe.id}-actions`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8"
                              onClick={() => resetRecipe(recipe.id)}
                              title="Mahlzeit neu auffüllen"
                              data-testid={`nutrition-recipe-${recipe.id}-reset`}
                            >
                              🔄
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-red-500"
                              onClick={() => deleteRecipe(recipe.id)}
                              data-testid={`nutrition-recipe-${recipe.id}-delete`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Remaining progress bar */}
                        <div className="mb-3" data-testid={`nutrition-recipe-${recipe.id}-progress`}>
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span data-testid={`nutrition-recipe-${recipe.id}-remaining`}>Übrig: {recipe.remaining_weight_g}g von {recipe.total_weight_g}g</span>
                            <span data-testid={`nutrition-recipe-${recipe.id}-percent`}>{percentRemaining}%</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${percentRemaining > 50 ? 'bg-green-500' : percentRemaining > 20 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${percentRemaining}%` }}
                              data-testid={`nutrition-recipe-${recipe.id}-progress-bar`}
                            />
                          </div>
                        </div>
                        
                        {/* Macros per 100g */}
                        <div className="flex flex-wrap gap-2 text-xs mb-3" data-testid={`nutrition-recipe-${recipe.id}-macros`}>
                          <span className="bg-orange-100 px-2 py-1 rounded" data-testid={`nutrition-recipe-${recipe.id}-macro-calories`}>
                            {recipe.calories_per_100g} kcal/100g
                          </span>
                          <span className="bg-red-100 px-2 py-1 rounded" data-testid={`nutrition-recipe-${recipe.id}-macro-protein`}>
                            P: {recipe.protein_per_100g}g/100g
                          </span>
                          <span className="bg-yellow-100 px-2 py-1 rounded" data-testid={`nutrition-recipe-${recipe.id}-macro-carbs`}>
                            K: {recipe.carbs_per_100g}g/100g
                          </span>
                          <span className="bg-blue-100 px-2 py-1 rounded" data-testid={`nutrition-recipe-${recipe.id}-macro-fat`}>
                            F: {recipe.fat_per_100g}g/100g
                          </span>
                        </div>
                        
                        {/* Ingredients list (collapsed) */}
                        <details className="text-xs text-gray-500 mb-3" data-testid={`nutrition-recipe-${recipe.id}-ingredients`}>
                          <summary className="cursor-pointer hover:text-gray-700" data-testid={`nutrition-recipe-${recipe.id}-ingredients-toggle`}>Zutaten anzeigen</summary>
                          <div className="mt-2 pl-2 border-l-2 border-gray-200">
                            {recipe.ingredients?.map((ing, idx) => (
                              <div key={idx} className="py-1" data-testid={`nutrition-recipe-${recipe.id}-ingredient-${idx}`}>
                                {ing.food_name} - {ing.amount_g}g ({ing.calories} kcal)
                              </div>
                            ))}
                          </div>
                        </details>
                        
                        {/* Take portion button */}
                        {recipe.remaining_weight_g > 0 ? (
                          <Button
                            className="w-full"
                            onClick={() => {
                              setSelectedRecipe(recipe);
                              setPortionAmount('');
                              setPortionDialogOpen(true);
                            }}
                            data-testid={`nutrition-recipe-${recipe.id}-take-portion`}
                          >
                            🍽️ Portion entnehmen
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => resetRecipe(recipe.id)}
                            data-testid={`nutrition-recipe-${recipe.id}-refill`}
                          >
                            Aufgebraucht - Klicken zum Auffüllen
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400" data-testid="nutrition-recipes-empty">
                  <div className="text-4xl mb-2">🍳</div>
                  <p className="font-medium" data-testid="nutrition-recipes-empty-title">Keine Mahlzeiten erstellt</p>
                  <p className="text-xs mt-1" data-testid="nutrition-recipes-empty-subtitle">Erstellen Sie ein Gericht aus mehreren Zutaten</p>
                  <Button className="mt-4" onClick={() => setRecipeDialogOpen(true)} data-testid="nutrition-recipes-empty-create">
                    <Plus className="w-4 h-4 mr-2" />
                    Erstes Rezept erstellen
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Saved Meals Tab */}
        <TabsContent value="saved" className="mt-4" data-testid="nutrition-tab-content-saved">
          <Card data-testid="nutrition-saved-card">
            <CardHeader className="py-3" data-testid="nutrition-saved-header">
              <CardTitle className="text-base" data-testid="nutrition-saved-title">Gespeicherte Mahlzeiten-Vorlagen</CardTitle>
              <p className="text-xs text-gray-500" data-testid="nutrition-saved-subtitle">Klicken Sie auf eine Vorlage, um sie heute hinzuzufügen</p>
            </CardHeader>
            <CardContent>
              {savedMeals.length > 0 ? (
                <div className="space-y-3">
                  {savedMeals.map((meal) => (
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                    <div key={meal.id} className="p-3 bg-gray-50 rounded-lg border hover:border-green-300 transition-colors" data-testid={`nutrition-saved-meal-${meal.id}`}>
                      <div className="flex items-start justify-between" data-testid={`nutrition-saved-meal-${meal.id}-content`}>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900" data-testid={`nutrition-saved-meal-${meal.id}-name`}>{meal.name}</h4>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1" data-testid={`nutrition-saved-meal-${meal.id}-macros`}>
                            <span className="bg-orange-100 px-2 py-0.5 rounded" data-testid={`nutrition-saved-meal-${meal.id}-calories`}>{meal.total_calories} kcal</span>
                            <span className="bg-red-100 px-2 py-0.5 rounded" data-testid={`nutrition-saved-meal-${meal.id}-protein`}>P: {meal.total_protein}g</span>
                            <span className="bg-yellow-100 px-2 py-0.5 rounded" data-testid={`nutrition-saved-meal-${meal.id}-carbs`}>K: {meal.total_carbs}g</span>
                            <span className="bg-blue-100 px-2 py-0.5 rounded" data-testid={`nutrition-saved-meal-${meal.id}-fat`}>F: {meal.total_fat}g</span>
                          </div>
                          <div className="mt-2 text-xs text-gray-400" data-testid={`nutrition-saved-meal-${meal.id}-items`}>
                            {meal.items?.length || 0} Lebensmittel: {meal.items?.slice(0, 3).map(i => i.food_name).join(', ')}{meal.items?.length > 3 ? '...' : ''}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1" data-testid={`nutrition-saved-meal-${meal.id}-actions`}>
                          <Select onValueChange={(mealType) => useSavedMeal(meal.id, mealType)}>
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`nutrition-saved-meal-${meal.id}-select`}>
                              <SelectValue placeholder="Hinzufügen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {visibleMealSlots.map(slot => (
                                <SelectItem key={slot} value={slot} data-testid={`nutrition-saved-meal-${meal.id}-option-${slot}`}>
                                  {MEAL_TYPES[slot]?.emoji} {MEAL_TYPES[slot]?.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 text-red-500"
                            onClick={() => deleteSavedMeal(meal.id)}
                            data-testid={`nutrition-saved-meal-${meal.id}-delete`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Löschen
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400" data-testid="nutrition-saved-empty">
                  <Apple className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p data-testid="nutrition-saved-empty-title">Keine gespeicherten Vorlagen</p>
                  <p className="text-xs mt-1" data-testid="nutrition-saved-empty-subtitle">Fügen Sie Lebensmittel hinzu und speichern Sie sie als Vorlage</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="mt-4" data-testid="nutrition-tab-content-stats">
          <Card data-testid="nutrition-stats-card">
            <CardHeader data-testid="nutrition-stats-header">
              <CardTitle className="text-base flex items-center gap-2" data-testid="nutrition-stats-title">
                <BarChart3 className="w-4 h-4" />
                Wochenübersicht
              </CardTitle>
            </CardHeader>
            <CardContent data-testid="nutrition-stats-content">
              {weeklyStats && weeklyStats.days_with_data > 0 ? (
                <div className="space-y-4" data-testid="nutrition-stats-body">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="nutrition-stats-summary-grid">
                    <div className="text-center p-3 bg-gray-50 rounded-lg" data-testid="nutrition-stats-avg-calories">
                      <p className="text-2xl font-bold text-gray-900" data-testid="nutrition-stats-avg-calories-value">{weeklyStats.averages.calories}</p>
                      <p className="text-xs text-gray-500" data-testid="nutrition-stats-avg-calories-label">⌀ Kalorien/Tag</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg" data-testid="nutrition-stats-avg-protein">
                      <p className="text-2xl font-bold text-red-600" data-testid="nutrition-stats-avg-protein-value">{weeklyStats.averages.protein}g</p>
                      <p className="text-xs text-gray-500" data-testid="nutrition-stats-avg-protein-label">⌀ Protein/Tag</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg" data-testid="nutrition-stats-avg-carbs">
                      <p className="text-2xl font-bold text-yellow-600" data-testid="nutrition-stats-avg-carbs-value">{weeklyStats.averages.carbs}g</p>
                      <p className="text-xs text-gray-500" data-testid="nutrition-stats-avg-carbs-label">⌀ Kohlenhydrate/Tag</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg" data-testid="nutrition-stats-avg-fat">
                      <p className="text-2xl font-bold text-blue-600" data-testid="nutrition-stats-avg-fat-value">{weeklyStats.averages.fat}g</p>
                      <p className="text-xs text-gray-500" data-testid="nutrition-stats-avg-fat-label">⌀ Fett/Tag</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2" data-testid="nutrition-stats-daily">
                    <p className="text-sm font-medium text-gray-700" data-testid="nutrition-stats-daily-title">Tägliche Übersicht</p>
                    {Object.entries(weeklyStats.daily_breakdown)
                      .sort((a, b) => b[0].localeCompare(a[0]))
                      .map(([date, data]) => (
                        <div key={date} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-testid={`nutrition-stats-day-${date}`}>
                          <span className="text-sm font-medium" data-testid={`nutrition-stats-day-${date}-label`}>
                            {new Date(date).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                          </span>
                          <div className="flex gap-4 text-xs" data-testid={`nutrition-stats-day-${date}-macros`}>
                            <span>{data.calories} kcal</span>
                            <span className="text-red-600">P: {data.protein}g</span>
                            <span className="text-yellow-600">K: {data.carbs}g</span>
                            <span className="text-blue-600">F: {data.fat}g</span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-400 py-4" data-testid="nutrition-stats-empty">
                  Keine Daten für die letzte Woche
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="nutrition-entry-dialog">
          <DialogHeader>
            <DialogTitle data-testid="nutrition-entry-dialog-title">
              {editingEntry ? 'Eintrag bearbeiten' : 'Lebensmittel hinzufügen'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Food Search */}
            {!editingEntry && (
              <div className="relative">
                <Label>Lebensmittel suchen</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                <Input
                    placeholder="z.B. Haferflocken, Joghurt..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchFood(e.target.value);
                    }}
                    className="pl-10"
                    data-testid="nutrition-entry-search-input"
                  />
                </div>
                
                {searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="nutrition-entry-search-results">
                    {searchResults.map((product, idx) => (
                      <button
                        key={idx}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        onClick={() => selectSearchResult(product)}
                        data-testid={`nutrition-entry-search-result-${idx}`}
                      >
                        <p className="font-medium" data-testid={`nutrition-entry-search-result-${idx}-name`}>{product.product_name}</p>
                        {product.brands && (
                          <p className="text-xs text-gray-500" data-testid={`nutrition-entry-search-result-${idx}-brand`}>{product.brands}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mahlzeit</Label>
                <Select 
                  value={formData.meal_type} 
                  onValueChange={(v) => setFormData({ ...formData, meal_type: v })}
                >
                  <SelectTrigger data-testid="nutrition-entry-meal-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(MEAL_TYPES).map(([key, config]) => (
                      <SelectItem key={key} value={key} data-testid={`nutrition-entry-meal-option-${key}`}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Menge (g)</Label>
                <Input
                  type="number"
                  value={formData.serving_size}
                  onChange={(e) => setFormData({ ...formData, serving_size: e.target.value })}
                  data-testid="nutrition-entry-serving-size"
                />
              </div>
            </div>

            {/* Time input - only shown when glucose tracking is enabled */}
            {glucoseTrackingEnabled && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-red-600 text-lg">🩸</span>
                  <Label className="text-red-700 font-medium">Uhrzeit für Blutzucker-Analyse</Label>
                </div>
                <Input
                  type="time"
                  value={formData.meal_time}
                  onChange={(e) => setFormData({ ...formData, meal_time: e.target.value })}
                  className="border-red-200 focus:ring-red-500"
                  data-testid="nutrition-entry-meal-time"
                />
                <p className="text-xs text-red-600 mt-1">
                  Die genaue Uhrzeit wird für die Blutzucker-Korrelation benötigt
                </p>
              </div>
            )}

            <div>
              <Label>Bezeichnung *</Label>
              <Input
                value={formData.food_name}
                onChange={(e) => setFormData({ ...formData, food_name: e.target.value })}
                placeholder="z.B. Haferflocken"
                data-testid="nutrition-entry-food-name"
              />
            </div>

            <div>
              <Label>Marke</Label>
              <Input
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                placeholder="z.B. Migros Bio"
                data-testid="nutrition-entry-brand"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label>Kalorien</Label>
                <Input
                  type="number"
                  value={formData.calories}
                  onChange={(e) => setFormData({ ...formData, calories: e.target.value })}
                  placeholder="kcal"
                  data-testid="nutrition-entry-calories"
                />
              </div>
              <div>
                <Label>Protein</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.protein}
                  onChange={(e) => setFormData({ ...formData, protein: e.target.value })}
                  placeholder="g"
                  data-testid="nutrition-entry-protein"
                />
              </div>
              <div>
                <Label>Kohlenhydrate</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.carbs}
                  onChange={(e) => setFormData({ ...formData, carbs: e.target.value })}
                  placeholder="g"
                  data-testid="nutrition-entry-carbs"
                />
              </div>
              <div>
                <Label>Fett</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fat}
                  onChange={(e) => setFormData({ ...formData, fat: e.target.value })}
                  placeholder="g"
                  data-testid="nutrition-entry-fat"
                />
              </div>
            </div>

            <div>
              <Label>Notizen</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optionale Notizen..."
                data-testid="nutrition-entry-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="nutrition-entry-cancel">
              Abbrechen
            </Button>
            <Button onClick={saveEntry} disabled={!formData.food_name} data-testid="nutrition-entry-save">
              {editingEntry ? 'Speichern' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goals Dialog */}
            <Dialog open={goalsDialogOpen} onOpenChange={setGoalsDialogOpen}>
        <DialogContent data-testid="nutrition-goals-dialog">
          <DialogHeader>
            <DialogTitle data-testid="nutrition-goals-dialog-title">Ernährungsziele festlegen</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Tägliche Kalorien</Label>
              <Input
                type="number"
                value={goalsForm.daily_calories}
                onChange={(e) => setGoalsForm({ ...goalsForm, daily_calories: parseInt(e.target.value) || 0 })}
                data-testid="nutrition-goals-calories"
              />
            </div>
            <div>
              <Label>Protein (g)</Label>
              <Input
                type="number"
                value={goalsForm.daily_protein}
                onChange={(e) => setGoalsForm({ ...goalsForm, daily_protein: parseFloat(e.target.value) || 0 })}
                data-testid="nutrition-goals-protein"
              />
            </div>
            <div>
              <Label>Kohlenhydrate (g)</Label>
              <Input
                type="number"
                value={goalsForm.daily_carbs}
                onChange={(e) => setGoalsForm({ ...goalsForm, daily_carbs: parseFloat(e.target.value) || 0 })}
                data-testid="nutrition-goals-carbs"
              />
            </div>
            <div>
              <Label>Fett (g)</Label>
              <Input
                type="number"
                value={goalsForm.daily_fat}
                onChange={(e) => setGoalsForm({ ...goalsForm, daily_fat: parseFloat(e.target.value) || 0 })}
                data-testid="nutrition-goals-fat"
              />
            </div>
            <div>
              <Label>💧 Tägliche Wassermenge (ml)</Label>
              <Input
                type="number"
                value={goalsForm.daily_water}
                onChange={(e) => setGoalsForm({ ...goalsForm, daily_water: parseInt(e.target.value) || 0 })}
                placeholder="2500"
                data-testid="nutrition-goals-water"
              />
              <p className="text-xs text-gray-500 mt-1">Empfohlen: 2000-3000ml pro Tag</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalsDialogOpen(false)} data-testid="nutrition-goals-cancel">
              Abbrechen
            </Button>
            <Button onClick={saveGoals} data-testid="nutrition-goals-save">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Meal Dialog */}
      <Dialog open={saveMealDialogOpen} onOpenChange={setSaveMealDialogOpen}>
        <DialogContent className="max-w-md" data-testid="nutrition-save-meal-dialog">
          <DialogHeader>
            <DialogTitle data-testid="nutrition-save-meal-title">Als Vorlage speichern</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Name der Vorlage *</Label>
              <Input
                value={newMealName}
                onChange={(e) => setNewMealName(e.target.value)}
                placeholder="z.B. Mein Frühstück, Protein-Snack..."
                data-testid="nutrition-save-meal-name"
              />
            </div>
            
            <div>
              <Label className="mb-2 block" data-testid="nutrition-save-meal-entries-label">Enthaltene Lebensmittel ({selectedEntriesForMeal.length})</Label>
              <div className="max-h-48 overflow-y-auto space-y-2 p-2 bg-gray-50 rounded" data-testid="nutrition-save-meal-entries">
                {selectedEntriesForMeal.map((entry, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border text-sm" data-testid={`nutrition-save-meal-entry-${idx}`}>
                    <span data-testid={`nutrition-save-meal-entry-${idx}-name`}>{entry.food_name}</span>
                    <span className="text-gray-500" data-testid={`nutrition-save-meal-entry-${idx}-calories`}>{entry.calories} kcal</span>
                  </div>
                ))}
              </div>
            </div>
            
            {selectedEntriesForMeal.length > 0 && (
              <div className="flex gap-3 text-sm bg-green-50 p-3 rounded" data-testid="nutrition-save-meal-totals">
                <span className="font-medium">Gesamt:</span>
                <span data-testid="nutrition-save-meal-total-calories">{selectedEntriesForMeal.reduce((s, e) => s + (e.calories || 0), 0)} kcal</span>
                <span data-testid="nutrition-save-meal-total-protein">P: {selectedEntriesForMeal.reduce((s, e) => s + (e.protein || 0), 0).toFixed(1)}g</span>
                <span data-testid="nutrition-save-meal-total-carbs">K: {selectedEntriesForMeal.reduce((s, e) => s + (e.carbs || 0), 0).toFixed(1)}g</span>
                <span data-testid="nutrition-save-meal-total-fat">F: {selectedEntriesForMeal.reduce((s, e) => s + (e.fat || 0), 0).toFixed(1)}g</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSaveMealDialogOpen(false); setNewMealName(''); setSelectedEntriesForMeal([]); }} data-testid="nutrition-save-meal-cancel">
              Abbrechen
            </Button>
            <Button onClick={createSavedMeal} disabled={!newMealName.trim() || selectedEntriesForMeal.length === 0} data-testid="nutrition-save-meal-confirm">
              <Star className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saved Meals Selection Dialog */}
      <Dialog open={savedMealsDialogOpen} onOpenChange={setSavedMealsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="nutrition-saved-dialog">
          <DialogHeader>
            <DialogTitle data-testid="nutrition-saved-dialog-title">Gespeicherte Vorlagen</DialogTitle>
          </DialogHeader>

          {savedMeals.length > 0 ? (
            <div className="space-y-3" data-testid="nutrition-saved-dialog-list">
              {savedMeals.map((meal) => (
                <div key={meal.id} className="p-3 bg-gray-50 rounded-lg border hover:border-green-300" data-testid={`nutrition-saved-dialog-item-${meal.id}`}>
                  <div className="flex justify-between items-start" data-testid={`nutrition-saved-dialog-item-${meal.id}-content`}>
                    <div>
                      <h4 className="font-medium" data-testid={`nutrition-saved-dialog-item-${meal.id}-name`}>{meal.name}</h4>
                      <div className="flex gap-2 text-xs text-gray-500 mt-1" data-testid={`nutrition-saved-dialog-item-${meal.id}-macros`}>
                        <span>{meal.total_calories} kcal</span>
                        <span>P: {meal.total_protein}g</span>
                        <span>K: {meal.total_carbs}g</span>
                        <span>F: {meal.total_fat}g</span>
                      </div>
                    </div>
                    <Select onValueChange={(mealType) => useSavedMeal(meal.id, mealType)}>
                      <SelectTrigger className="w-36" data-testid={`nutrition-saved-dialog-item-${meal.id}-select`}>
                        <SelectValue placeholder="Hinzufügen als..." />
                      </SelectTrigger>
                      <SelectContent>
                        {visibleMealSlots.map(slot => (
                          <SelectItem key={slot} value={slot} data-testid={`nutrition-saved-dialog-item-${meal.id}-option-${slot}`}>
                            {MEAL_TYPES[slot]?.emoji} {MEAL_TYPES[slot]?.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400" data-testid="nutrition-saved-dialog-empty">
              <p data-testid="nutrition-saved-dialog-empty-text">Keine gespeicherten Vorlagen</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recipe Creation Dialog */}
      <Dialog open={recipeDialogOpen} onOpenChange={setRecipeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="nutrition-recipe-dialog">
          <DialogHeader>
            <DialogTitle data-testid="nutrition-recipe-dialog-title">🍳 Neues Rezept erstellen</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Name der Mahlzeit *</Label>
              <Input
                value={newRecipeName}
                onChange={(e) => setNewRecipeName(e.target.value)}
                placeholder="z.B. Hähnchen-Reis-Bowl, Pasta Bolognese..."
                data-testid="nutrition-recipe-name"
              />
            </div>

            {/* Ingredient search */}
            <div className="relative">
              <Label>Zutat suchen und hinzufügen</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="z.B. Hähnchenbrust, Reis, Zwiebeln..."
                  value={ingredientSearch}
                  onChange={(e) => {
                    setIngredientSearch(e.target.value);
                    searchIngredient(e.target.value);
                  }}
                  className="pl-10"
                  data-testid="nutrition-recipe-search-input"
                />
              </div>
              
              {ingredientSearchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="nutrition-recipe-search-results">
                  {ingredientSearchResults.map((product, idx) => (
                    <button
                      key={idx}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-b"
                      onClick={() => addIngredientToRecipe(product, 100)}
                      data-testid={`nutrition-recipe-search-result-${idx}`}
                    >
                      <p className="font-medium" data-testid={`nutrition-recipe-search-result-${idx}-name`}>{product.product_name || product.name}</p>
                      <p className="text-xs text-gray-500" data-testid={`nutrition-recipe-search-result-${idx}-meta`}>
                        {product.nutriments?.['energy-kcal_100g'] || product.nutrition_per_100g?.calories || '?'} kcal/100g
                        {(product.brands || product.brand) && ` • ${product.brands || product.brand}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Added ingredients */}
            <div>
              <Label className="mb-2 block" data-testid="nutrition-recipe-ingredients-label">Zutaten ({recipeIngredients.length})</Label>
              {recipeIngredients.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="nutrition-recipe-ingredients-list">
                  {recipeIngredients.map((ing) => (
                    <div key={ing.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded border" data-testid={`nutrition-recipe-ingredient-${ing.id}`}>
                      <div className="flex-1">
                        <p className="font-medium text-sm" data-testid={`nutrition-recipe-ingredient-${ing.id}-name`}>{ing.food_name}</p>
                        <p className="text-xs text-gray-500" data-testid={`nutrition-recipe-ingredient-${ing.id}-macros`}>{ing.calories} kcal • P: {ing.protein}g • K: {ing.carbs}g • F: {ing.fat}g</p>
                      </div>
                      <div className="flex items-center gap-2" data-testid={`nutrition-recipe-ingredient-${ing.id}-actions`}>
                        <Input
                          type="number"
                          value={ing.amount_g}
                          onChange={(e) => updateIngredientAmount(ing.id, parseFloat(e.target.value) || 0)}
                          className="w-20 h-8 text-center"
                          data-testid={`nutrition-recipe-ingredient-${ing.id}-amount`}
                        />
                        <span className="text-sm text-gray-500">g</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500"
                          onClick={() => removeIngredientFromRecipe(ing.id)}
                          data-testid={`nutrition-recipe-ingredient-${ing.id}-remove`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-400 bg-gray-50 rounded" data-testid="nutrition-recipe-ingredients-empty">
                  Suchen Sie Zutaten und fügen Sie sie hinzu
                </div>
              )}
            </div>

            {/* Totals */}
            {recipeIngredients.length > 0 && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-testid="nutrition-recipe-totals">
                <h4 className="font-semibold text-green-900 mb-2" data-testid="nutrition-recipe-totals-title">Gesamtwerte</h4>
                <div className="grid grid-cols-5 gap-2 text-center" data-testid="nutrition-recipe-totals-grid">
                  <div data-testid="nutrition-recipe-total-weight">
                    <p className="text-2xl font-bold text-gray-900">
                      {recipeIngredients.reduce((s, i) => s + i.amount_g, 0)}g
                    </p>
                    <p className="text-xs text-gray-500">Gewicht</p>
                  </div>
                  <div data-testid="nutrition-recipe-total-calories">
                    <p className="text-2xl font-bold text-orange-600">
                      {recipeIngredients.reduce((s, i) => s + i.calories, 0)}
                    </p>
                    <p className="text-xs text-gray-500">kcal</p>
                  </div>
                  <div data-testid="nutrition-recipe-total-protein">
                    <p className="text-2xl font-bold text-red-600">
                      {recipeIngredients.reduce((s, i) => s + i.protein, 0).toFixed(1)}g
                    </p>
                    <p className="text-xs text-gray-500">Protein</p>
                  </div>
                  <div data-testid="nutrition-recipe-total-carbs">
                    <p className="text-2xl font-bold text-yellow-600">
                      {recipeIngredients.reduce((s, i) => s + i.carbs, 0).toFixed(1)}g
                    </p>
                    <p className="text-xs text-gray-500">Kohlenhydrate</p>
                  </div>
                  <div data-testid="nutrition-recipe-total-fat">
                    <p className="text-2xl font-bold text-blue-600">
                      {recipeIngredients.reduce((s, i) => s + i.fat, 0).toFixed(1)}g
                    </p>
                    <p className="text-xs text-gray-500">Fett</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setRecipeDialogOpen(false); setNewRecipeName(''); setRecipeIngredients([]); }} data-testid="nutrition-recipe-cancel">
              Abbrechen
            </Button>
            <Button onClick={saveRecipe} disabled={!newRecipeName.trim() || recipeIngredients.length === 0} data-testid="nutrition-recipe-save">
              Rezept erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Take Portion Dialog */}
      <Dialog open={portionDialogOpen} onOpenChange={setPortionDialogOpen}>
        <DialogContent className="max-w-md" data-testid="nutrition-portion-dialog">
          <DialogHeader>
            <DialogTitle data-testid="nutrition-portion-dialog-title">🍽️ Portion entnehmen</DialogTitle>
          </DialogHeader>

          {selectedRecipe && (
            <div className="space-y-4" data-testid="nutrition-portion-body">
              <div className="p-3 bg-gray-50 rounded-lg" data-testid="nutrition-portion-summary">
                <h4 className="font-semibold" data-testid="nutrition-portion-recipe-name">{selectedRecipe.name}</h4>
                <p className="text-sm text-gray-500" data-testid="nutrition-portion-remaining">
                  Verfügbar: {selectedRecipe.remaining_weight_g}g von {selectedRecipe.total_weight_g}g
                </p>
              </div>

              <div>
                <Label>Portionsgrösse (g) *</Label>
                <Input
                  type="number"
                  value={portionAmount}
                  onChange={(e) => setPortionAmount(e.target.value)}
                  placeholder={`Max. ${selectedRecipe.remaining_weight_g}g`}
                  max={selectedRecipe.remaining_weight_g}
                  data-testid="nutrition-portion-amount"
                />
                <div className="flex gap-2 mt-2" data-testid="nutrition-portion-presets">
                  {[100, 150, 200, 250, 300].map(amount => (
                    amount <= selectedRecipe.remaining_weight_g && (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setPortionAmount(amount.toString())}
                        data-testid={`nutrition-portion-preset-${amount}`}
                      >
                        {amount}g
                      </Button>
                    )
                  ))}
                </div>
              </div>

              <div>
                <Label>Hinzufügen als</Label>
                <Select value={portionMealType} onValueChange={setPortionMealType}>
                  <SelectTrigger data-testid="nutrition-portion-meal-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleMealSlots.map(slot => (
                      <SelectItem key={slot} value={slot} data-testid={`nutrition-portion-option-${slot}`}>
                        {MEAL_TYPES[slot]?.emoji} {MEAL_TYPES[slot]?.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preview calculation */}
              {portionAmount && parseFloat(portionAmount) > 0 && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200" data-testid="nutrition-portion-preview">
                  <h4 className="font-medium text-green-900 mb-2" data-testid="nutrition-portion-preview-title">Berechnet für {portionAmount}g:</h4>
                  <div className="grid grid-cols-4 gap-2 text-center text-sm" data-testid="nutrition-portion-preview-grid">
                    <div data-testid="nutrition-portion-preview-calories">
                      <p className="font-bold text-orange-600">
                        {Math.round(selectedRecipe.calories_per_100g * parseFloat(portionAmount) / 100)}
                      </p>
                      <p className="text-xs text-gray-500">kcal</p>
                    </div>
                    <div data-testid="nutrition-portion-preview-protein">
                      <p className="font-bold text-red-600">
                        {(selectedRecipe.protein_per_100g * parseFloat(portionAmount) / 100).toFixed(1)}g
                      </p>
                      <p className="text-xs text-gray-500">Protein</p>
                    </div>
                    <div data-testid="nutrition-portion-preview-carbs">
                      <p className="font-bold text-yellow-600">
                        {(selectedRecipe.carbs_per_100g * parseFloat(portionAmount) / 100).toFixed(1)}g
                      </p>
                      <p className="text-xs text-gray-500">Kohlenhydrate</p>
                    </div>
                    <div data-testid="nutrition-portion-preview-fat">
                      <p className="font-bold text-blue-600">
                        {(selectedRecipe.fat_per_100g * parseFloat(portionAmount) / 100).toFixed(1)}g
                      </p>
                      <p className="text-xs text-gray-500">Fett</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setPortionDialogOpen(false); setSelectedRecipe(null); }} data-testid="nutrition-portion-cancel">
              Abbrechen
            </Button>
            <Button 
              onClick={takePortionFromRecipe} 
              disabled={!portionAmount || parseFloat(portionAmount) <= 0 || parseFloat(portionAmount) > (selectedRecipe?.remaining_weight_g || 0)}
              data-testid="nutrition-portion-confirm"
            >
              Portion hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerNutrition;
